import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SiteNav from './SiteNav';
import Footer from './Footer';
import './App.css';

type GalleryPageProps = {
  title: string;
  images: string[];
};

const MIN_SCALE = 0.62;
const MAX_SCALE = 1.5;
const EASE = 0.1; // lower = smoother/slower glide, higher = snappier
const SETTLE_DELAY = 160; // ms of no new scroll/wheel input before gently snapping to nearest center

export default function GalleryPage({ title, images }: GalleryPageProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const targetScroll = useRef(0);
  const rafId = useRef<number | null>(null);
  // True while native touch scrolling (drag or momentum) is actively
  // driving scrollLeft — our own lerp write in tick() steps aside during
  // this window so it never fights the browser's own motion.
  const isTouching = useRef(false);
  // Set to true immediately before OUR OWN code writes to scrollLeft.
  // Writing scrollLeft fires a 'scroll' event just like a real user
  // scroll does — without this flag, the onScroll handler below (added
  // to track native touch scrolling) couldn't tell the difference and
  // would immediately reset targetScroll back to whatever we just wrote,
  // one frame at a time. That was the exact bug that broke desktop wheel
  // scrolling last round: every lerp step's own 'scroll' event was
  // cancelling the lerp's progress before it could glide anywhere.
  const isProgrammaticScroll = useRef(false);
  const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    document.title = `${title} — Positive Grain`;
  }, [title]);

  // Continuous, scroll-position-driven zoom: whichever photo sits closest
  // to the horizontal center of the track is scaled up the most, with a
  // smooth falloff toward the edges — no opacity/blur dimming, every photo
  // stays fully bright and sharp, only its SIZE changes as you scroll.
  //
  // Movement itself is eased (lerp toward a target scrollLeft each frame)
  // instead of jumping 1:1 with each wheel tick, so it glides like the
  // detroit.paris / betteroff.studio references instead of stepping.
  //
  // IMPORTANT: scale is written straight to each element's own
  // style.transform via the ref, NOT through React state. Driving this via
  // setState would re-render the whole page (including the fixed PG logo
  // and nav) up to 60x/second while scrolling, which was subtly jittering
  // that nearby fixed text. Bypassing React for this per-frame write keeps
  // everything else on the page completely untouched.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    // Decides whether JS's own "snap to nearest center" runs at all
    // (desktop wheel-scrolling still needs it — there's no native
    // scroll-snap trigger from wheel input) or steps aside because CSS
    // scroll-snap is already handling docking natively (touch).
    const isTouchDevice = window.matchMedia('(hover: none)').matches;

    // Center the FIRST photo on load, the same way any photo gets
    // centered once you scroll to it — offsetLeft + half its own width,
    // minus half the viewport, clamped to the valid scroll range. (The
    // previous version deliberately left it offset near the left edge;
    // centering here now matches every other photo's resting state.)
    //
    // This depends on the first item's actual rendered width, which in
    // turn depends on the image's natural aspect ratio being known
    // (height:100%; width:auto sizing) — if the browser hasn't finished
    // decoding the image yet at mount time, offsetWidth can read 0 or a
    // placeholder size, producing a wrong (uncentered) result. Loading
    // it eagerly (see the img below) covers most of this, but as a
    // belt-and-suspenders fix, if it genuinely isn't loaded yet we also
    // re-run centering once its 'load' event fires.
    const centerOnFirstItem = () => {
      const firstItem = itemRefs.current[0];
      if (!firstItem) return;
      const maxScroll = track.scrollWidth - track.clientWidth;
      const centered = Math.min(
        maxScroll,
        Math.max(
          0,
          firstItem.offsetLeft + firstItem.offsetWidth / 2 - track.clientWidth / 2
        )
      );
      track.scrollLeft = centered;
      targetScroll.current = centered;
    };

    centerOnFirstItem();
    const firstImg = itemRefs.current[0]?.querySelector('img');
    if (firstImg && !firstImg.complete) {
      firstImg.addEventListener('load', centerOnFirstItem, { once: true });
    }

    const updateScales = () => {
      const containerRect = track.getBoundingClientRect();
      const centerX = containerRect.left + containerRect.width / 2;

      itemRefs.current.forEach((el: HTMLDivElement | null) => {
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const itemCenter = rect.left + rect.width / 2;
        const distance = Math.abs(itemCenter - centerX);
        const maxDistance = containerRect.width / 2 + rect.width / 2;
        const proximity = Math.max(0, 1 - distance / maxDistance);
        const scale = MIN_SCALE + proximity * (MAX_SCALE - MIN_SCALE);
        el.style.transform = `scale(${scale})`;
      });
    };

    // Finds whichever photo currently sits closest to the container's
    // center and points targetScroll at the exact scrollLeft that would
    // center it precisely — the existing lerp then glides there on its
    // own over the next few frames. Called once input has been quiet for
    // SETTLE_DELAY (see scheduleSettle), so it never fights an
    // in-progress wheel or touch gesture.
    const snapToNearestCenter = () => {
      const containerRect = track.getBoundingClientRect();
      const centerX = containerRect.left + containerRect.width / 2;
      let nearestDistance = Infinity;
      let snappedScroll = targetScroll.current;

      itemRefs.current.forEach((el: HTMLDivElement | null) => {
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const itemCenter = rect.left + rect.width / 2;
        const distance = Math.abs(itemCenter - centerX);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          snappedScroll = track.scrollLeft + (itemCenter - centerX);
        }
      });

      const maxScroll = track.scrollWidth - track.clientWidth;
      targetScroll.current = Math.min(
        maxScroll,
        Math.max(0, snappedScroll)
      );
    };

    const scheduleSettle = () => {
      if (settleTimer.current) clearTimeout(settleTimer.current);
      settleTimer.current = setTimeout(snapToNearestCenter, SETTLE_DELAY);
    };

    const tick = () => {
      if (!isTouching.current) {
        const current = track.scrollLeft;
        const diff = targetScroll.current - current;
        const next =
          Math.abs(diff) > 0.5 ? current + diff * EASE : targetScroll.current;
        if (next !== current) {
          isProgrammaticScroll.current = true;
          track.scrollLeft = next;
        }
      }
      updateScales();
      rafId.current = requestAnimationFrame(tick);
    };

    const onWheel = (e: WheelEvent) => {
      if (e.deltaY === 0) return;
      e.preventDefault();
      const maxScroll = track.scrollWidth - track.clientWidth;
      targetScroll.current = Math.min(
        maxScroll,
        Math.max(0, targetScroll.current + e.deltaY)
      );
      scheduleSettle();
    };

    // Touch: no manual scrollLeft writes during the gesture itself —
    // overflow-x:auto already gives native, smooth, momentum-based touch
    // scrolling for free, and CSS scroll-snap (see App.css) now handles
    // the actual docking natively once the gesture ends — regardless of
    // whether it was a quick flick or a longer drag, so there's no JS
    // timing/distance heuristic needed here anymore (a previous version
    // tried classifying flick-vs-drag in JS; native scroll-snap
    // supersedes that entirely and is more reliable). touchstart still
    // flips isTouching immediately, closing the tiny gap before the
    // first native 'scroll' event would otherwise arrive.
    const onTouchStart = () => {
      isTouching.current = true;
      if (settleTimer.current) {
        clearTimeout(settleTimer.current);
        settleTimer.current = null;
      }
    };

    const onScroll = () => {
      if (isProgrammaticScroll.current) {
        // This 'scroll' event was caused by OUR OWN write above (lerp),
        // not by the user — ignore it and clear the flag for next time.
        isProgrammaticScroll.current = false;
        return;
      }
      // Genuinely native (touch drag/momentum, or a trackpad two-finger
      // swipe the browser scrolled directly): trust the browser's
      // position outright and keep our own writes paused for a short
      // window after the last such event, so the lerp never fights it.
      isTouching.current = true;
      targetScroll.current = track.scrollLeft;
      if (settleTimer.current) clearTimeout(settleTimer.current);
      settleTimer.current = setTimeout(() => {
        isTouching.current = false;
        // Only nudge toward a computed "nearest center" ourselves when
        // this DIDN'T come from touch — on touch, CSS scroll-snap (see
        // App.css) already snapped the track to an exact item by the
        // time this fires, so recalculating and re-nudging here would
        // just be a second, slightly different opinion fighting the
        // native snap's already-correct result.
        if (!isTouchDevice) snapToNearestCenter();
      }, SETTLE_DELAY);
    };

    updateScales();
    rafId.current = requestAnimationFrame(tick);
    track.addEventListener('wheel', onWheel, { passive: false });
    track.addEventListener('touchstart', onTouchStart, { passive: true });
    track.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updateScales);
    return () => {
      track.removeEventListener('wheel', onWheel);
      track.removeEventListener('touchstart', onTouchStart);
      track.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updateScales);
      if (rafId.current !== null) cancelAnimationFrame(rafId.current);
      if (settleTimer.current) clearTimeout(settleTimer.current);
    };
  }, [images.length]);

  return (
    <div className="gallery-page">
      <SiteNav />
      <Link to="/" className="gallery-page-back" aria-label="Back">
        ←
      </Link>

      <div className="gallery-hscroll" ref={trackRef}>
        {images.map((src, i) => (
          <div
            key={src}
            ref={(el: HTMLDivElement | null) => {
              itemRefs.current[i] = el;
            }}
            className="gallery-hscroll-item"
            style={{ transform: `scale(${MIN_SCALE})` }}
          >
            <img
              src={src}
              alt=""
              className="gallery-hscroll-img"
              loading={i === 0 ? 'eager' : 'lazy'}
              decoding="async"
            />
          </div>
        ))}
      </div>

      <Footer />
    </div>
  );
}